import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../App';
import AiLocked from '../components/AiLocked';
import { Loader2, CalendarCheck, Plus, Trash2, Briefcase } from 'lucide-react';

const STATUS_COLORS = {
  applied: 'bg-blue-100 text-blue-800',
  interview: 'bg-amber-100 text-amber-800',
  offer: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-100 text-gray-600',
};

export default function JobTrackerPage() {
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [openDay, setOpenDay] = useState(1);
  const [form, setForm] = useState({ company: '', role: '', status: 'applied', followup_date: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const load = () => axios.get(`${API}/ai/tracker`).then(r => setTracker(r.data))
    .catch(e => { if (e.response?.status === 403) setLocked(true); })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const toggle = async (day, taskId, done) => {
    setTracker(t => ({ ...t, days: t.days.map(d => d.day === day ? { ...d, tasks: d.tasks.map(tk => tk.id === taskId ? { ...tk, done } : tk) } : d) }));
    try { await axios.post(`${API}/ai/tracker/task`, { day, task_id: taskId, done }); }
    catch (e) { toast.error('Could not save'); load(); }
  };

  const addApp = async () => {
    if (!form.company.trim() || !form.role.trim()) { toast.error('Company and role required'); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/ai/tracker/application`, form);
      setForm({ company: '', role: '', status: 'applied', followup_date: '', notes: '' });
      toast.success('Application logged');
      load();
    } catch (e) { toast.error('Could not add'); } finally { setAdding(false); }
  };

  const delApp = async (id) => {
    try { await axios.delete(`${API}/ai/tracker/application/${id}`); load(); } catch (e) { toast.error('Could not delete'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl w-full">
        <div className="flex items-center gap-2 mb-6">
          <CalendarCheck className="w-6 h-6 text-[#CE1126]" />
          <h1 className="text-2xl font-bold">30-Day Job Search Tracker</h1>
        </div>

        {locked || !tracker ? (
          <AiLocked title="30-Day Job Search Tracker" />
        ) : (
          <>
            {/* Progress summary */}
            <Card className="p-5 mb-6" data-testid="tracker-summary">
              <div className="flex flex-wrap gap-6 items-center">
                <div>
                  <p className="text-xs text-gray-500">Daily tasks done</p>
                  <p className="text-2xl font-bold">{tracker.summary.done_tasks}<span className="text-base text-gray-400">/{tracker.summary.total_tasks}</span></p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Applications logged</p>
                  <p className="text-2xl font-bold">{tracker.summary.applications}</p>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <p className="text-xs text-gray-500 mb-1">Progress {tracker.summary.progress_pct}%</p>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-[#009639]" style={{ width: `${tracker.summary.progress_pct}%` }} />
                  </div>
                </div>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {/* 30-day checklist */}
              <Card className="p-4" data-testid="tracker-days">
                <h3 className="font-bold mb-3">Daily Plan</h3>
                <div className="flex flex-wrap gap-1 mb-3">
                  {tracker.days.map(d => {
                    const done = d.tasks.every(t => t.done);
                    return (
                      <button key={d.day} onClick={() => setOpenDay(d.day)}
                        className={`w-8 h-8 rounded-md text-xs font-medium ${openDay === d.day ? 'ring-2 ring-[#CE1126]' : ''} ${done ? 'bg-[#009639] text-white' : 'bg-gray-100 text-gray-700'}`}
                        data-testid={`tracker-day-${d.day}`}>{d.day}</button>
                    );
                  })}
                </div>
                {tracker.days.filter(d => d.day === openDay).map(d => (
                  <div key={d.day} className="space-y-2">
                    <p className="text-sm font-semibold">Day {d.day}</p>
                    {d.tasks.map(t => (
                      <label key={t.id} className="flex items-start gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={t.done} onChange={e => toggle(d.day, t.id, e.target.checked)} className="accent-[#009639] mt-0.5" data-testid={`task-${t.id}`} />
                        <span className={t.done ? 'line-through text-gray-400' : ''}>{t.text}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </Card>

              {/* Applications */}
              <Card className="p-4" data-testid="tracker-apps">
                <h3 className="font-bold mb-3 flex items-center gap-1"><Briefcase className="w-4 h-4" />Application Log</h3>
                <div className="space-y-2 mb-4 max-h-56 overflow-y-auto">
                  {(tracker.applications || []).length === 0 && <p className="text-sm text-gray-400">No applications yet.</p>}
                  {(tracker.applications || []).map(a => (
                    <div key={a.id} className="flex items-center justify-between border rounded-md px-3 py-2" data-testid={`app-${a.id}`}>
                      <div>
                        <p className="text-sm font-medium">{a.role} · {a.company}</p>
                        {a.followup_date && <p className="text-xs text-gray-500">Follow up: {a.followup_date}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[a.status] || 'bg-gray-100'}`}>{a.status}</span>
                        <button onClick={() => delApp(a.id)} data-testid={`del-app-${a.id}`}><Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 border-t pt-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Company" className="h-9 rounded-md border border-gray-300 px-2 text-sm" data-testid="app-company" />
                    <input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="Role" className="h-9 rounded-md border border-gray-300 px-2 text-sm" data-testid="app-role" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="h-9 rounded-md border border-gray-300 px-2 text-sm" data-testid="app-status">
                      <option value="applied">Applied</option><option value="interview">Interview</option><option value="offer">Offer</option><option value="rejected">Rejected</option>
                    </select>
                    <input type="date" value={form.followup_date} onChange={e => setForm({ ...form, followup_date: e.target.value })} className="h-9 rounded-md border border-gray-300 px-2 text-sm" data-testid="app-followup" />
                  </div>
                  <Button className="btn-uae-green w-full" onClick={addApp} disabled={adding} data-testid="app-add-btn">
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Log Application</>}
                  </Button>
                </div>
              </Card>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
